import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Container,
  InputAdornment
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { resendVerificationEmail } from '../utils/api';

const ResendVerification = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      await resendVerificationEmail(email);
      setMessage('If an account with that email exists and needs verification, a verification email has been sent.');
      setSubmitted(true);
    } catch (err) {
      // Always show success message for security (prevent enumeration)
      setMessage('If an account with that email exists and needs verification, a verification email has been sent.');
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        py={4}
      >
        <Paper elevation={3} sx={{ width: '100%', p: 4 }}>
          <Box textAlign="center" mb={4}>
            <EmailIcon color="primary" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Resend Verification Email
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter your email address to receive a new verification link
            </Typography>
          </Box>

          <Card>
            <CardContent>
              {!submitted ? (
                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    error={email && !isValidEmail(email)}
                    helperText={
                      email && !isValidEmail(email) 
                        ? 'Please enter a valid email address'
                        : 'We\'ll send a verification link to this email address'
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 3 }}
                  />

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading || !email || !isValidEmail(email)}
                    startIcon={loading ? null : <SendIcon />}
                    size="large"
                    sx={{ mb: 2 }}
                  >
                    {loading ? 'Sending...' : 'Send Verification Email'}
                  </Button>
                </Box>
              ) : (
                <Box textAlign="center">
                  <Alert severity="success" sx={{ mb: 3 }}>
                    {message}
                  </Alert>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Please check your email inbox and spam folder for the verification link.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSubmitted(false);
                      setEmail('');
                      setMessage('');
                    }}
                    startIcon={<EmailIcon />}
                  >
                    Send Another Email
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          <Box mt={3} textAlign="center">
            <Button
              component={Link}
              to="/login"
              startIcon={<ArrowBackIcon />}
              color="primary"
            >
              Back to Login
            </Button>
          </Box>

          <Box mt={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              📧 Check your email for a verification link. The link expires in 24 hours for security.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ResendVerification;